import React, { useState } from 'react';
import axios from 'axios';
import { Truck } from 'lucide-react';
import API_URL from '../../config';
import '../../App.css';

const DVIRModal = ({ isOpen, onClose, vehicle, onSuccess }) => {
  const [formData, setFormData] = useState({
    inspectionDate: new Date().toISOString().split('T')[0],
    inspectionType: 'pre-trip',
    checklist: {
      brakes: false,
      tires: false,
      lights: false,
      mirrors: false,
      horn: false,
      wipers: false,
      fluidLevels: false,
      coupling: false,
      cargo: false,
    },
    hasDefects: false,
    defectsDescription: '',
    defectsRepaired: false,
    repairDate: '',
    repairNotes: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const checklistItems = [
    { key: 'brakes', label: 'Brakes' },
    { key: 'tires', label: 'Tires' },
    { key: 'lights', label: 'Lights' },
    { key: 'mirrors', label: 'Mirrors' },
    { key: 'horn', label: 'Horn' },
    { key: 'wipers', label: 'Wipers' },
    { key: 'fluidLevels', label: 'Fluid Levels' },
    { key: 'coupling', label: 'Coupling/Fifth Wheel' },
    { key: 'cargo', label: 'Cargo Securement' },
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('checklist.')) {
      const checklistKey = name.split('.')[1];
      setFormData((prev) => ({
        ...prev,
        checklist: {
          ...prev.checklist,
          [checklistKey]: checked,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/vehicles/${vehicle.id}/dvir`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      onSuccess();
      onClose();
      // Reset form
      setFormData({
        inspectionDate: new Date().toISOString().split('T')[0],
        inspectionType: 'pre-trip',
        checklist: {
          brakes: false,
          tires: false,
          lights: false,
          mirrors: false,
          horn: false,
          wipers: false,
          fluidLevels: false,
          coupling: false,
          cargo: false,
        },
        hasDefects: false,
        defectsDescription: '',
        defectsRepaired: false,
        repairDate: '',
        repairNotes: '',
      });
    } catch (err) {
      console.error('Error submitting DVIR:', err);
      alert('Failed to submit inspection report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen || !vehicle) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="dvir-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Daily Vehicle Inspection Report</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="dvir-vehicle-info">
          <span className="dvir-vehicle-name">
            <Truck size={18} style={{ verticalAlign: 'middle', marginRight: 6 }} /> {vehicle.nickname || `${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          </span>
          <span className="dvir-vehicle-plate">
            {vehicle.licensePlate} ({vehicle.licensePlateState})
          </span>
        </div>

        <form onSubmit={handleSubmit} className="dvir-form">
          <div className="form-section">
            <div className="form-row">
              <div className="input-group">
                <input
                  type="date"
                  name="inspectionDate"
                  value={formData.inspectionDate}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                />
                <label className="form-label">Inspection Date</label>
              </div>

              <div className="input-group">
                <select
                  name="inspectionType"
                  value={formData.inspectionType}
                  onChange={handleInputChange}
                  className="form-input"
                  required
                >
                  <option value="pre-trip">Pre-Trip</option>
                  <option value="post-trip">Post-Trip</option>
                </select>
                <label className="form-label">Inspection Type</label>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3 className="form-section-title">Inspection Checklist</h3>
            <div className="dvir-checklist">
              {checklistItems.map((item) => (
                <div key={item.key} className="dvir-item">
                  <input
                    type="checkbox"
                    id={`checklist-${item.key}`}
                    name={`checklist.${item.key}`}
                    checked={formData.checklist[item.key]}
                    onChange={handleInputChange}
                    className="dvir-checkbox"
                  />
                  <label htmlFor={`checklist-${item.key}`} className="dvir-label">
                    {item.label}
                  </label>
                </div>
              ))}
            </div>
          </div>

          <div className="form-section">
            <div className="input-group toggle-group">
              <label className="toggle-label">
                <input
                  type="checkbox"
                  name="hasDefects"
                  checked={formData.hasDefects}
                  onChange={handleInputChange}
                  className="toggle-input"
                />
                <span className="toggle-text">Any defects found?</span>
              </label>
            </div>

            {formData.hasDefects && (
              <>
                <div className="input-group">
                  <textarea
                    name="defectsDescription"
                    value={formData.defectsDescription}
                    onChange={handleInputChange}
                    className="form-input textarea"
                    placeholder="Describe the defects..."
                    rows={4}
                    required={formData.hasDefects}
                  />
                  <label className="form-label">Defects Description</label>
                </div>

                <div className="input-group toggle-group">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      name="defectsRepaired"
                      checked={formData.defectsRepaired}
                      onChange={handleInputChange}
                      className="toggle-input"
                    />
                    <span className="toggle-text">Defects repaired?</span>
                  </label>
                </div>

                {formData.defectsRepaired && (
                  <>
                    <div className="input-group">
                      <input
                        type="date"
                        name="repairDate"
                        value={formData.repairDate}
                        onChange={handleInputChange}
                        className="form-input"
                        required={formData.defectsRepaired}
                      />
                      <label className="form-label">Repair Date</label>
                    </div>

                    <div className="input-group">
                      <textarea
                        name="repairNotes"
                        value={formData.repairNotes}
                        onChange={handleInputChange}
                        className="form-input textarea"
                        placeholder="Repair notes..."
                        rows={3}
                      />
                      <label className="form-label">Repair Notes</label>
                    </div>
                  </>
                )}
              </>
            )}
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="modal-button cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="modal-button save"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Inspection'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DVIRModal;

